import { ClientResponse, DiscountCodeInfo, DiscountCodeState } from '@commercetools/platform-sdk';
import xor from 'lodash/xor';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';

import { ctApi } from '@/api';
import { useCart } from '@/composables/useCart';
import {
  ProductsByTags,
  ProductsByTagsProjectionResult,
  ProductsByTagsProjectionSearchResponse,
  Variables,
} from '@/graphql/productsByTags';
import {
  CartDiscountMaybeStandardFields,
  getExpandedCartDiscounts,
  TAG_REFERENCE_DISCOUNT_PREDICATE_PATTERN,
  TAG_REFERENCE_EXTRACT_PATTERN,
} from '@/lib/cart/discounts';
import { reportError } from '@/utils/reportError';

export interface BundlePromo {
  cartDiscount: CartDiscountMaybeStandardFields;
  matchesCart: boolean;
  productKeys: Set<string>;
}

function getDiscountCode(discountCode: DiscountCodeInfo) {
  return discountCode.discountCode.obj;
}

export function buildQueryFilters(targets: string[]): string {
  const filters = targets.map((target) => `"${target}"`).join(',');
  return `variants.attributes.tagReferences.id:${filters}`;
}

export const parsePredicate = (predicate: string): string[] => {
  const tagIds: string[] = [];

  if (!TAG_REFERENCE_DISCOUNT_PREDICATE_PATTERN.test(predicate)) {
    return tagIds;
  }

  const tagReferenceRegex = new RegExp(TAG_REFERENCE_EXTRACT_PATTERN);

  let matching = tagReferenceRegex.exec(predicate);
  while (matching !== null) {
    const [, tagId] = matching;
    tagIds.push(tagId);
    matching = tagReferenceRegex.exec(predicate);
  }

  return tagIds;
};

export const useBundlePromo = defineStore('bundlePromo', () => {
  const store = useStore();
  const { cart } = useCart(store);

  const isPending = ref(false);
  const productSearchResults = ref<ProductsByTagsProjectionResult[]>([]);

  const acceptableStates: DiscountCodeState[] = ['DoesNotMatchCart', 'MatchesCart'];
  const relatedDiscounts = computed(
    () =>
      cart.value?.discountCodes?.filter(
        (discountCodeInfo) =>
          acceptableStates.includes(discountCodeInfo.state) &&
          getExpandedCartDiscounts(getDiscountCode(discountCodeInfo)).some((cartDiscount) =>
            cartDiscount?.cartPredicate?.match(TAG_REFERENCE_DISCOUNT_PREDICATE_PATTERN),
          ),
      ) ?? [],
  );

  const bundlePromos = computed(() => {
    if (productSearchResults.value.length === 0) {
      return [];
    }

    const cartDiscountsByTagId = new Map<
      string,
      {
        cartDiscount: CartDiscountMaybeStandardFields;
        matchesCart: boolean;
      }[]
    >();

    relatedDiscounts.value.forEach((discount) => {
      getExpandedCartDiscounts(getDiscountCode(discount)).forEach((cartDiscount) => {
        if (!cartDiscount.cartPredicate) return;

        const targetedTagIds = parsePredicate(cartDiscount.cartPredicate);

        targetedTagIds.forEach((tagId) => {
          if (!cartDiscountsByTagId.has(tagId)) {
            cartDiscountsByTagId.set(tagId, []);
          }
          cartDiscountsByTagId.get(tagId)!.push({
            cartDiscount,
            matchesCart: discount.state === 'MatchesCart',
          });
        });
      });
    });

    const bundlePromoByTagId = new Map<string, BundlePromo>();

    productSearchResults.value.forEach((product) => {
      const [{ value: tagReferences }] = product.masterVariant.attributesRaw;
      if (tagReferences.length === 0) return;

      tagReferences.forEach((tag) => {
        const discounts = cartDiscountsByTagId.get(tag.id);

        if (!discounts) return;
        // Note: we decided to get the first discount because we
        // don't see an use case for multiple discounts on the same tag yet.
        const { cartDiscount, matchesCart } = discounts[0];

        const existingPromo = bundlePromoByTagId.get(tag.id);

        if (existingPromo) {
          existingPromo.productKeys.add(product.key);
        } else {
          bundlePromoByTagId.set(tag.id, {
            cartDiscount,
            matchesCart,
            productKeys: new Set([product.key]),
          });
        }
      });
    });

    return Array.from(bundlePromoByTagId.values());
  });

  const promosByProductKey = computed(() => {
    const map = new Map<string, BundlePromo[]>();

    bundlePromos.value.forEach((info) => {
      info.productKeys.forEach((productKey) => {
        map.set(productKey, [...(map.get(productKey) || []), info]);
      });
    });

    return map;
  });

  const targetedTagIds = computed(() => {
    const targets: string[] = [];

    relatedDiscounts.value.forEach((discount) => {
      getDiscountCode(discount)?.cartDiscounts?.forEach(({ obj: cartDiscount }) => {
        if (cartDiscount?.cartPredicate) {
          const parsed = parsePredicate(cartDiscount.cartPredicate);
          targets.push(...parsed);
        }
      });
    });

    return [...new Set(targets)];
  });

  const fetchTargetedProducts = async () => {
    isPending.value = true;
    try {
      const productsResponse = (await ctApi
        .graphql()
        .post({
          body: {
            query: ProductsByTags,
            variables: {
              limit: 500,
              locale: 'en',
              tagFilters: buildQueryFilters(targetedTagIds.value),
            } satisfies Variables,
          },
        })
        .execute()) as unknown as ClientResponse<ProductsByTagsProjectionSearchResponse>;

      productSearchResults.value = productsResponse.body.data.productProjectionSearch.results;
    } catch (err) {
      reportError(err, 'Failed to fetch products for discount targeting', {
        tagIds: targetedTagIds.value.join(','),
      });
    } finally {
      isPending.value = false;
    }
  };

  watch(
    targetedTagIds,
    async (newTagIds, oldTagIds = []) => {
      if (xor(newTagIds, oldTagIds).length === 0) {
        return;
      }

      if (newTagIds.length === 0) {
        productSearchResults.value = [];
        return;
      }

      await fetchTargetedProducts();
    },
    { immediate: true },
  );

  return {
    bundlePromos,
    isPending,
    productSearchResults,
    promosByProductKey,
    relatedDiscounts,
    targetedTagIds,
  };
});
